import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar, CheckSquare, FileKey, Plus } from "lucide-react";
import AddEvent from "../../components/calendar/events/AddEvent";
import ModalMeet from "../../components/calendar/ModalMeet";
import NewTask from "../../components/TasksPage/NewTask";
import AdminRequestModal from "../../components/userComponents/userProfileComponents/AdminRequestModal";

interface AddNewBtnProps {
  variant?: "button" | "menu";
  onActionTriggered?: () => void;
}

const AddNewBtn: React.FC<AddNewBtnProps> = ({
  variant = "button",
  onActionTriggered,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showAdminRequestModal, setShowAdminRequestModal] = useState(false);

  useEffect(() => {
    if (variant !== "button" || !isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, variant]);

  useEffect(() => {
    if (variant !== "button" || !isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, variant]);

  const handleAction = (action: () => void) => {
    action();
    setIsOpen(false);
    onActionTriggered?.();
  };

  const actionItems = (
    <div className="flex flex-col gap-0.5">
      <button
        type="button"
        onClick={() => handleAction(() => setShowAddEvent(true))}
        className="group flex w-full items-center gap-2.5 rounded-xl border-none bg-transparent p-2 text-left transition-colors hover:!bg-[#F9F6FF] dark:hover:!bg-purple-400/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7E57C2]/30"
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-50 dark:bg-purple-400/15 text-[#7E57C2] dark:text-purple-300">
          <Calendar size={16} />
        </div>
        <div className="flex min-w-0 flex-col">
          <strong className="mb-0.5 text-sm font-semibold leading-tight text-gray-900 dark:text-slate-100">
            Add New Meeting
          </strong>
          <span className="text-xs text-gray-500 dark:text-slate-400">Schedule a meeting</span>
        </div>
      </button>

      <button
        type="button"
        onClick={() => handleAction(() => setShowAddTaskModal(true))}
        className="group flex w-full items-center gap-2.5 rounded-xl border-none bg-transparent p-2 text-left transition-colors hover:!bg-[#F9F6FF] dark:hover:!bg-purple-400/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7E57C2]/30"
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-50 dark:bg-purple-400/15 text-[#7E57C2] dark:text-purple-300">
          <CheckSquare size={16} />
        </div>
        <div className="flex min-w-0 flex-col">
          <strong className="mb-0.5 text-sm font-semibold leading-tight text-gray-900 dark:text-slate-100">
            Add New Task
          </strong>
          <span className="text-xs text-gray-500 dark:text-slate-400">Create a task</span>
        </div>
      </button>

      <button
        type="button"
        onClick={() => handleAction(() => setShowAdminRequestModal(true))}
        className="group flex w-full items-center gap-2.5 rounded-xl border-none bg-transparent p-2 text-left transition-colors hover:!bg-[#F9F6FF] dark:hover:!bg-purple-400/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7E57C2]/30"
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-50 dark:bg-purple-400/15 text-[#7E57C2] dark:text-purple-300">
          <FileKey size={16} />
        </div>
        <div className="flex min-w-0 flex-col">
          <strong className="mb-0.5 text-sm font-semibold leading-tight text-gray-900 dark:text-slate-100">
            Add Admin Request
          </strong>
          <span className="text-xs text-gray-500 dark:text-slate-400">Request time off or a file</span>
        </div>
      </button>
    </div>
  );

  const modalLayer =
    typeof document !== "undefined"
      ? createPortal(
          <>
            {showAddEvent && (
              <ModalMeet
                isOpen={showAddEvent}
                onClose={() => setShowAddEvent(false)}
              >
                <AddEvent
                  event={null}
                  onClose={() => setShowAddEvent(false)}
                />
              </ModalMeet>
            )}

            {showAddTaskModal && (
              <ModalMeet
                isOpen={showAddTaskModal}
                onClose={() => setShowAddTaskModal(false)}
                widthClass="max-w-[600px]"
              >
                <NewTask onClose={() => setShowAddTaskModal(false)} />
              </ModalMeet>
            )}

            {showAdminRequestModal && (
              <AdminRequestModal
                handleClose={() => setShowAdminRequestModal(false)}
                show={showAdminRequestModal}
              />
            )}
          </>,
          document.body
        )
      : null;

  if (variant === "menu") {
    return (
      <>
        {actionItems}
        {modalLayer}
      </>
    );
  }

  return (
    <>
      <div className="relative inline-block text-left" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          className="flex h-8 min-w-[110px] items-center justify-center gap-1.5 rounded-lg border-none bg-[#7E57C2] px-3 py-1.5 font-medium text-white shadow-sm transition-colors hover:bg-[#6d47b0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7E57C2]/30 md:h-9 md:py-2"
          aria-haspopup="menu"
          aria-expanded={isOpen}
        >
          <Plus size={16} strokeWidth={2.5} />
          <span className="text-sm">Add New</span>
        </button>

        <div
          className={`dark-popup absolute right-0 top-[110%] z-[100] w-64 origin-top-right rounded-xl border border-gray-100 bg-white dark:bg-zinc-900 p-1.5 shadow-[0_8px_30px_rgb(0,0,0,0.12)] dark:shadow-[0_16px_40px_rgba(0,0,0,0.4)] transition-all duration-200 ease-out ${
            isOpen
              ? "visible translate-y-0 scale-100 opacity-100"
              : "invisible -translate-y-2 scale-95 opacity-0 pointer-events-none"
          }`}
          role="menu"
        >
          {actionItems}
        </div>
      </div>

      {modalLayer}
    </>
  );
};

export default AddNewBtn;
